import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

export type PurchaseOrderStatus = $Enums.PurchaseOrderStatus;

export type PurchaseOrderFacts = {
  /** undefined = not hydrated yet; only the always-editable fields open. */
  status: PurchaseOrderStatus | undefined;
  hasVendor: boolean;
  /** Any line's lot locks Deliver To (`purchaseOrderLotLocksDeliverTo`); undefined = not known, treated as none. */
  hasLots?: boolean;
  /**
   * The operation facts below are read only by the door that asks the operation; every one of
   * them reads undefined as "not known", which never denies.
   */
  /** delete: this order is its organization's default purchase order template. */
  isDefaultTemplate?: boolean;
  /** delete: a live line on this order is sourced for a sales order (an external line). */
  hasSalesOrderLines?: boolean;
  /** revert out of PENDING_LIQUIDATION or SHIPPED: a bill attached to this order is paid. */
  hasPaidBill?: boolean;
  /** revert out of RECEIVING: a lot this order received has been used (`AllowMidReceivingShipping` only). */
  lotsUsed?: boolean;
};

/**
 * Lot statuses that hold nothing at the delivery location: reverting a receipt parks the order's lots
 * in DRAFT, and the next receipt re-homes them to the order's current Deliver To.
 */
export const PURCHASE_ORDER_PLACEHOLDER_LOT_STATUSES: readonly $Enums.LotStatus[] = ['DRAFT'];

/** The `hasLots` test for one line's lot: any received status locks, and so does a status not known. */
export const purchaseOrderLotLocksDeliverTo = (status: $Enums.LotStatus | null | undefined): boolean =>
  status == null || !PURCHASE_ORDER_PLACEHOLDER_LOT_STATUSES.includes(status);

export const PURCHASE_ORDER_FIELDS = [
  'vendor',
  'vendorSo',
  'shipment',
  'receiveDate',
  'orderDate',
  'terms',
  'submittedAt',
  'salesTerms',
  'buyer',
  'businessEntity',
  'deliverTo',
  'shipDate',
  'businessEntityContact',
  'lineItems',
  'expenses',
  'currency',
  'subsidiary',
  'temperatureMin',
  'temperatureMax',
  'freightCompany',
  'label',
] as const;

export type PurchaseOrderField = (typeof PURCHASE_ORDER_FIELDS)[number];

/**
 * The lifecycle operations the policy explains. Every OTHER event's availability stays with the
 * state machine (`api/src/services/purchaseOrders/purchaseOrder.states.ts`), which is the authority.
 */
export const PURCHASE_ORDER_OPS = ['cancel', 'revert'] as const;

export type PurchaseOrderOp = (typeof PURCHASE_ORDER_OPS)[number];

const TO_SUBMITTED: readonly PurchaseOrderStatus[] = ['DRAFT', 'SUBMITTED'];
const TO_RECEIVING: readonly PurchaseOrderStatus[] = [...TO_SUBMITTED, 'RECEIVING'];
const TO_PENDING_LIQUIDATION: readonly PurchaseOrderStatus[] = [...TO_RECEIVING, 'SHIPPED', 'PENDING_LIQUIDATION'];

/** The statuses in which a purchase order's products can change — also gates SO lines linked to it. */
export const PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES: readonly PurchaseOrderStatus[] = TO_RECEIVING;

/** The statuses in which a purchase order's charges can change. */
export const PURCHASE_ORDER_EXPENSES_EDITABLE_STATUSES: readonly PurchaseOrderStatus[] = TO_PENDING_LIQUIDATION;

/**
 * The statuses the state machine accepts `cancel` from — also the predicate of the cancel write,
 * which re-checks it so a concurrent transition cannot be cancelled over.
 */
export const PURCHASE_ORDER_CANCELLABLE_STATUSES: readonly PurchaseOrderStatus[] = TO_SUBMITTED;

const inWindow = (window: readonly PurchaseOrderStatus[], status: PurchaseOrderStatus | undefined): boolean =>
  status !== undefined && window.includes(status);

const statusAllowsField = (status: PurchaseOrderStatus | undefined, field: PurchaseOrderField): boolean => {
  switch (field) {
    case 'businessEntity':
    case 'businessEntityContact':
      return inWindow(TO_SUBMITTED, status);
    case 'vendor':
    case 'deliverTo':
      return inWindow(TO_RECEIVING, status);
    case 'lineItems':
      return inWindow(PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES, status);
    case 'currency':
    case 'receiveDate':
    case 'shipDate':
    case 'shipment':
    case 'salesTerms':
    case 'buyer':
    case 'terms':
    case 'vendorSo':
    case 'temperatureMin':
    case 'temperatureMax':
    case 'freightCompany':
      return inWindow(TO_PENDING_LIQUIDATION, status);
    case 'expenses':
      return inWindow(PURCHASE_ORDER_EXPENSES_EDITABLE_STATUSES, status);
    case 'label':
      return status === 'DRAFT';
    case 'submittedAt':
    case 'orderDate':
    case 'subsidiary':
      return true;
  }
};

const decideField = (facts: PurchaseOrderFacts, field: PurchaseOrderField): Decision => {
  if (field === 'label') {
    return statusAllowsField(facts.status, field)
      ? allow()
      : deny('PURCHASE_ORDER_LABEL_LOCKED', 'The label can only be changed in Draft.');
  }

  if (!statusAllowsField(facts.status, field)) {
    return deny(
      'PURCHASE_ORDER_FIELD_LOCKED',
      facts.status === undefined
        ? 'This field is locked while the purchase order status is unavailable.'
        : `This field is locked once the purchase order is ${getStatusLabel(facts.status)}.`
    );
  }

  if (field === 'deliverTo' && facts.hasLots === true) {
    return deny('PURCHASE_ORDER_DELIVER_TO_HAS_LOTS', 'Deliver To is locked once products have lots.');
  }

  if (field === 'shipment' && !facts.hasVendor) {
    return deny('PURCHASE_ORDER_SHIPMENT_VENDOR_REQUIRED', 'Select a vendor to edit shipping.');
  }

  return allow();
};

/** Delete guards in the server's order: default template → attached to a sales order → Draft only. */
const decideDelete = (facts: PurchaseOrderFacts): Decision => {
  if (facts.isDefaultTemplate === true) {
    return deny('PURCHASE_ORDER_DELETE_LOCKED', "The default template can't be deleted.");
  }

  if (facts.hasSalesOrderLines === true) {
    return deny('PURCHASE_ORDER_DELETE_LOCKED', "Can't delete: this order is linked to a sales order.");
  }

  if (facts.status === 'DRAFT') return allow();

  // Not hydrated yet: there is no status to name, so only the rule itself is stated.
  return facts.status === undefined
    ? deny('PURCHASE_ORDER_DELETE_LOCKED', 'Only Draft purchase orders can be deleted.')
    : deny(
        'PURCHASE_ORDER_DELETE_LOCKED',
        `Only Draft purchase orders can be deleted. This one is ${getStatusLabel(facts.status)}.`
      );
};

/**
 * `revert` is judged on the edge the status implies; whether a status accepts `revert` at all is
 * the state machine's call. Each edge has its own fact and its own server sentence.
 */
const decideRevert = (facts: PurchaseOrderFacts): Decision => {
  switch (facts.status) {
    case 'RECEIVING':
      return facts.lotsUsed === true
        ? deny('PURCHASE_ORDER_REVERT_LOCKED', "Can't revert: lots from this order are already in use.")
        : allow();
    case 'PENDING_LIQUIDATION':
      return facts.hasPaidBill === true
        ? deny('PURCHASE_ORDER_REVERT_LOCKED', "Can't revert: a bill on this purchase order is paid.")
        : allow();
    case 'SHIPPED':
      return facts.hasPaidBill === true
        ? deny('PURCHASE_ORDER_REVERT_LOCKED', "Can't revert: a payable on this purchase order is paid.")
        : allow();
    default:
      return allow();
  }
};

/** UI action names used by the header and its confirmation controls. */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  submit: 'Submit',
  receive: 'Receive',
  mark_as_received: 'Confirm Receipt',
  mark_as_shipped: 'Confirm Shipped',
  close: 'Close',
  revert: 'Revert Purchase Order',
  cancel: 'Cancel Purchase Order',
  uncancel: 'Uncancel Purchase Order',
};

export const purchaseOrderEventUnavailableMessage = (
  status: PurchaseOrderStatus | undefined,
  event: string
): string => {
  const action = EVENT_LABELS[event] ?? 'This action';
  return status === undefined
    ? `${action} isn't available while the purchase order status is unavailable.`
    : `${action} isn't available while the purchase order is ${getStatusLabel(status)}.`;
};

/** The state machine owns the cancellable window; the denial uses its UI action label. */
const decideCancel = (facts: PurchaseOrderFacts): Decision => {
  if (facts.status !== undefined && PURCHASE_ORDER_CANCELLABLE_STATUSES.includes(facts.status)) return allow();

  return deny('PURCHASE_ORDER_CANCEL_LOCKED', purchaseOrderEventUnavailableMessage(facts.status, 'cancel'));
};

const decideOperation = (facts: PurchaseOrderFacts, op: PurchaseOrderOp): Decision => {
  switch (op) {
    case 'cancel':
      return decideCancel(facts);
    case 'revert':
      return decideRevert(facts);
  }
};

const fieldRule = (facts: PurchaseOrderFacts, action: Action<PurchaseOrderField, PurchaseOrderOp>): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  vendor: fieldRule,
  vendorSo: fieldRule,
  shipment: fieldRule,
  receiveDate: fieldRule,
  orderDate: fieldRule,
  terms: fieldRule,
  submittedAt: fieldRule,
  salesTerms: fieldRule,
  buyer: fieldRule,
  businessEntity: fieldRule,
  deliverTo: fieldRule,
  shipDate: fieldRule,
  businessEntityContact: fieldRule,
  lineItems: fieldRule,
  expenses: fieldRule,
  currency: fieldRule,
  subsidiary: fieldRule,
  temperatureMin: fieldRule,
  temperatureMax: fieldRule,
  freightCompany: fieldRule,
  label: fieldRule,
} satisfies Record<PurchaseOrderField, typeof fieldRule>;

export const purchaseOrderPolicy: Policy<PurchaseOrderFacts, PurchaseOrderField, PurchaseOrderOp> = definePolicy<
  PurchaseOrderFacts,
  PurchaseOrderField,
  PurchaseOrderOp
>({
  kind: 'purchase order',
  fields: PURCHASE_ORDER_FIELDS,
  ops: PURCHASE_ORDER_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type === 'delete') return decideDelete(facts);
    if (action.type === 'perform') return decideOperation(facts, action.op);
    return allow();
  },
  field: fieldRules,
});
